import { useRef, useState, useLayoutEffect, useCallback } from "react";
import cn from "mcn";

import { CloseIcon } from "../icons/CloseIcon";

export const ModalFloating = ({
  children,
  title,
  constrainRef,
  width,
  height,
  onClose,
  footer,
  "data-testid": dataTestId,
  zIndex,
}: {
  children: React.ReactNode;
  title: string;
  constrainRef?: React.RefObject<HTMLDivElement>;
  width?: number | string;
  height?: number | string;
  onClose?: () => void;
  footer?: React.ReactNode;
  "data-testid"?: string;
  zIndex?: number | string;
}) => {
  const hasConstraint = !!constrainRef?.current;
  const { dragging, ref: dragRef, start: dragStart } = useDraggable({ constrainRef, position: "center" });

  return (
    <div
      ref={dragRef}
      data-testid={dataTestId ?? "modal"}
      className={cn(
        "rounded-lg border outline-outline overflow-hidden select-none [&_input]:select-text [&_textarea]:select-text",
        "z-[30] bg-card flex flex-col items-stretch",
        "transition-[opacity,box-shadow] duration-100",
        [hasConstraint, "absolute", "fixed"],
        [dragging, "opacity-90"],
        [
          dragging,
          "dark:shadow-[5px_3px_7px_1px_rgba(100,100,100,0.25)] shadow-[5px_3px_7px_1px_rgba(0,0,0,0.25)]",
          "dark:shadow-[4px_2px_6px_-1px_rgba(100,100,100,0.25)] shadow-[4px_2px_6px_-1px_rgba(0,0,0,0.25)]",
        ],
        zIndex ? `z-[${zIndex}]!` : "",
      )}
      style={{
        width,
        height,
        maxWidth: "var(--Max-Width-3xl, 576px)",
        maxHeight: "var(--Max-Width-3xl, 576px)",
        zIndex,
      }}
    >
      {/* Header */}
      <div className="w-full text-foreground items-center self-stretch py-3 px-[18px] gap-[9px] grid grid-cols-[auto_1fr_auto]">
        <span className="text-foreground text-[13.5px] font-semibold leading-[21px] whitespace-nowrap">{title}</span>
        <div
          className={cn("h-full w-full", dragging ? "cursor-grabbing" : "cursor-grab")}
          onMouseDown={dragStart}
          onTouchStart={dragStart}
        />
        {onClose && <CloseIcon onClick={onClose} className="w-7 h-7" />}
      </div>
      {/* Content */}
      <div className="flex-1 w-full min-h-0 flex flex-col min-w-0">{children}</div>
      {/* Footer */}
      {footer && (
        <div className="flex w-full text-foreground items-center self-stretch py-3 px-[18px] gap-[9px] border-t border-outline">
          {footer}
        </div>
      )}
    </div>
  );
};

// --- useDraggable hook -----------------------------------------------------
const useDraggable = ({
  constrainRef,
  position,
}: {
  constrainRef?: React.RefObject<HTMLDivElement>;
  position?: "center";
}) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const stateRef = useRef({ x: 0, y: 0 });
  const offsetRef = useRef({ x: 0, y: 0 });

  const getConstraintRect = useCallback(() => {
    const c = constrainRef?.current?.getBoundingClientRect();
    if (c) return c;
    return document.documentElement.getBoundingClientRect();
  }, [constrainRef]);

  const start = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setDragging(true);
    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
    const rect = getConstraintRect();
    stateRef.current = {
      x: clientX - offsetRef.current.x - rect.left,
      y: clientY - offsetRef.current.y - rect.top,
    };
  };

  const move = useCallback(() => {
    if (!elementRef.current) return;
    const rect = getConstraintRect();
    elementRef.current.style.left = `${rect.left + offsetRef.current.x}px`;
    elementRef.current.style.top = `${rect.top + offsetRef.current.y}px`;
  }, [getConstraintRect]);

  const update = useCallback(
    (x: number, y: number) => {
      offsetRef.current = { x, y };
      move();
    },
    [move],
  );

  useLayoutEffect(() => {
    if (!dragging) return;

    const onMove = (e: MouseEvent | TouchEvent) => {
      const pos = isTouchEvent(e)
        ? { x: e.touches[0].clientX, y: e.touches[0].clientY }
        : { x: e.clientX, y: e.clientY };

      if (!elementRef.current) return;
      const rect = getConstraintRect();
      const modalRect = elementRef.current.getBoundingClientRect();

      let newX = pos.x - stateRef.current.x - rect.left;
      let newY = pos.y - stateRef.current.y - rect.top;

      newX = Math.max(0, Math.min(newX, rect.width - modalRect.width));
      newY = Math.max(0, Math.min(newY, rect.height - modalRect.height));

      update(newX, newY);
    };

    const onUp = () => setDragging(false);

    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("mouseup", onUp, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onUp, { passive: true });
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [dragging, update, getConstraintRect]);

  useLayoutEffect(() => {
    if (!elementRef.current || !position) return;
    const rect = getConstraintRect();
    const modalRect = elementRef.current.getBoundingClientRect();
    update((rect.width - modalRect.width) / 2, (rect.height - modalRect.height) / 2);
  }, [position, update, getConstraintRect]);

  useLayoutEffect(() => {
    const target = constrainRef?.current ?? document.documentElement;
    const observer = new ResizeObserver(() => {
      if (!elementRef.current) return;
      const rect = getConstraintRect();
      const modalRect = elementRef.current.getBoundingClientRect();
      const { x, y } = offsetRef.current;
      const nx = Math.max(0, Math.min(x, rect.width - modalRect.width));
      const ny = Math.max(0, Math.min(y, rect.height - modalRect.height));
      update(nx, ny);
    });
    observer.observe(target);
    return () => observer.disconnect();
  }, [constrainRef, getConstraintRect, update]);

  return { dragging, ref: elementRef, start };
};

const isTouchEvent = (e: MouseEvent | TouchEvent): e is TouchEvent => {
  return "touches" in e;
};
